<template>
  <section class="transaction-table-wrapper" v-if="tableRows && tableRows.length > 0">
    <div class="table-container">
      <table class="transaction-table" id="transaction-table">
        <thead class="transaction-table__head">
          <tr>
            <th
              class="transaction-table__header-row"
              ref="tableHead"
              :style="gridStyling"
            >
              <template v-for="(header, index) in tableHeaders" :key="'header' + index">
                <div
                  v-if="header !== 'misc'"
                  class="transaction-table__header-cell"
                  :class="{
                    'transaction-table__header-cell--center': header === 'Action',
                  }"
                >
                  {{ header }}
                </div>
              </template>
            </th>
          </tr>
        </thead>
        <tbody class="transaction-table__body">
          <tr>
            <td ref="tableRow">
              <div
                class="transaction-table__row"
                v-for="(details, index) in tableRows"
                :key="'row' + index"
                :style="gridStyling"
              >
                <span class="transaction-table__icon">
                  <template v-if="details?.type">
                    <div
                      v-if="
                        details?.type.includes('Mint') ||
                        details?.type.includes('Fund') ||
                        details?.type.includes('Carry') ||
                        details?.type.includes('Fund') ||
                        details?.type.includes('Buy') ||
                        details?.type.includes('Trade Reversal')
                      "
                      class="transaction-table__icon-box transaction-table__icon-box--credit"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 12.6667V3.33337" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M3.33334 8.00004L8 3.33337L12.6667 8.00004" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                    <div
                      v-if="
                        details?.type.includes('Withdraw') ||
                        details?.type.includes('Debit Wallet') ||
                        details?.type.includes('Send') ||
                        details?.type.includes('Fee') ||
                        details?.type.includes('Sell') ||
                        details?.type.includes('Plan')
                      "
                      class="transaction-table__icon-box transaction-table__icon-box--debit"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 3.33337V12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M12.6667 8.00004L8 12.6667L3.33334 8.00004" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </div>
                  </template>
                  <div v-else class="transaction-table__icon-box transaction-table__icon-box--default">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 3.33337V12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M12.6667 8.00004L8 12.6667L3.33334 8.00004" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </span>
                <template
                  v-for="(detail, key, index) in details"
                  :key="'column' + index"
                >
                  <div
                    v-if="key !== 'misc' && key !== 'type'"
                    class="transaction-table__cell"
                  >
                    <p
                      v-html="detail"
                      :class="[
                        key === 'status' ? 'status-badge status-badge--' + String(detail).toLowerCase() : null,
                      ]"
                      class="transaction-table__cell-text"
                    ></p>
                  </div>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
        <ViewTransactionModal
          v-if="showViewTransactionModal"
          class="transaction-modal"
          @close="showViewTransactionModal = !showViewTransactionModal"
        >
          <template v-slot:content>
            <div class="modal-header">
              <h3 class="modal-header__title">Transaction Details</h3>
              <p class="modal-header__ref">{{ transactionRef }}</p>
            </div>
            <div class="modal-divider"></div>
            <div class="modal-row">
              <div class="modal-row__item">
                <p class="modal-row__label">Amount</p>
                <p class="modal-row__value">{{ amount }}</p>
              </div>
              <div class="modal-row__item modal-row__item--right">
                <p class="modal-row__label">Type</p>
                <p class="modal-row__value">{{ type }}</p>
              </div>
            </div>
            <div class="modal-row">
              <div class="modal-row__item">
                <p class="modal-row__label">Date</p>
                <p class="modal-row__value">{{ date }}</p>
              </div>
              <div class="modal-row__item modal-row__item--right">
                <p class="modal-row__label">Status</p>
                <p
                  class="modal-row__value"
                  :class="{
                    'modal-row__value--success': status === 'Completed',
                    'modal-row__value--warning': status === 'Pending',
                    'modal-row__value--error': status === 'Failed',
                  }"
                >
                  {{ status }}
                </p>
              </div>
            </div>
          </template>
        </ViewTransactionModal>
      </table>
    </div>
    <div class="pagination-wrapper" v-if="showPagination">
      <Pagination
        :totalPage="totalPage"
        :currentPage="currentPage"
        :maxVisible="9"
        @pageChange="gotoPage"
      />
    </div>
    <Modal
      v-if="showRefundModal"
      :show="showRefundModal"
      class="refund-modal"
      @close-modal="showRefundModal = false"
    >
      <Loader v-if="loading" />
      <div class="refund-modal__content">
        <div class="refund-modal__header">
          <h1 class="refund-modal__title">Request Refund</h1>
        </div>
        <form class="refund-form" @submit.prevent="validateForm">
          <div class="refund-form__info">
            <p class="refund-form__label">Refund Amount</p>
            <p class="refund-form__hint">Zero charges</p>
          </div>
          <div class="refund-form__input-wrapper">
            <p
              class="refund-form__error"
              v-if="v$.refundAmount.$dirty && v$.refundAmount.required.$invalid"
            >
              {{ v$.refundAmount.$errors[0].$message }}
            </p>
            <p
              class="refund-form__error"
              v-if="v$.refundAmount.$dirty && v$.refundAmount.maxValue.$invalid"
            >
              Amount is greater than Transaction Amount
              <span class="refund-form__error-amount">${{ maxRefundAmount }}</span>
            </p>
            <input
              type="number"
              name="refundAmount"
              v-model="refundAmount"
              id="refundAmount"
              @input="v$.refundAmount.$touch()"
              placeholder="50"
              class="refund-form__input"
              :class="{
                'refund-form__input--error':
                  (v$.refundAmount.$dirty &&
                    v$.refundAmount.maxValue.$invalid) ||
                  (v$.refundAmount.$dirty && v$.refundAmount.required.$invalid),
              }"
            />
          </div>
          <div class="refund-form__checkbox-wrapper">
            <input
              type="checkbox"
              id="requestMax"
              class="refund-form__checkbox"
              v-model="requestMaxAMount"
            />
            <label for="requestMax" class="refund-form__checkbox-label">
              Withdraw full amount:
              <span class="refund-form__checkbox-amount">{{
                formatAmountToDollar(maxRefundAmount, 2, "USD")
              }}</span>
            </label>
          </div>
          <button type="submit" class="refund-form__submit">
            Send request
          </button>
        </form>
      </div>
    </Modal>
  </section>
</template>

<script>
  import ViewTransactionModal from "@/components/general/MediumModal";
  import Pagination from "@/components/navigation/Pagination";
  import Modal from "@/components/general/Modal";
  import { formatAmountToDollar } from "@/utils/helpers.js";
  import { mapActions } from "vuex";
  import useValidate from "@vuelidate/core";
  import { required, maxValue } from "@vuelidate/validators";
  export default {
    name: "TransactionTable",

    components: {
      Pagination,
      ViewTransactionModal,
      Modal,
    },

    props: {
      tableHeaders: { type: Array, default: () => [], required: true },
      tableRows: { type: Array, default: () => [], required: true },
      columnNumber: { type: Number, default: () => 0, required: true },
      columnWidth: { type: Number, default: () => 0, required: true },
      totalPage: { type: Number, default: () => 0, required: true },
      currentPage: { type: Number, default: () => 0, required: true },
      showPagination: { type: Boolean, default: () => true, required: false },
    },

    emits: ["go-to-page", "refund-request"],

    data() {
      return {
        rows: [],
        v$: useValidate(),
        type: "",
        loading: false,
        colunmSize: "",
        showViewTransactionModal: false,
        transactionRef: "",
        discription: "",
        image: "",
        amount: "",
        type: "",
        date: "",
        status: "",
        showActions: false,
        activeRow: null,
        maxRefundAmount: "",
        refundTransactionId: 0,
        showRefundModal: false,
        refundAmount: "",
        requestMaxAMount: false,
        availableStatuses: ["Completed", "Failed", "Pending"],
      };
    },
    validations() {
      return {
        refundAmount: {
          required,
          maxValue: maxValue(this.maxRefundAmount),
        },
      };
    },
    computed: {
      gridStyling() {
        return {
          "grid-template-columns": `60px minmax(180px, 1fr) minmax(200px, 1fr) minmax(120px, auto) minmax(100px, auto) repeat(${
            this.columnNumber
          }, minmax(${this.columnWidth}px, auto))`,
        };
      },
    },
    watch: {
      refundAmount(val) {
        if (val !== this.maxRefundAmount) {
          this.requestMaxAMount = false;
        }
      },
      requestMaxAMount(val) {
        if (val) {
          this.refundAmount = this.maxRefundAmount;
        }
      },
    },

    methods: {
      ...mapActions("organisationModule", ["getRefund"]),
      ...mapActions(["showToast"]),
      formatAmountToDollar,
      viewTransaction(details) {
        this.transactionRef = details.reference.split(">")[1].split("<")[0];
        this.description = details.details.split(">")[1].split("<")[0];
        this.amount = details.amount;
        const misc = details.misc.split("/");
        this.type = misc[2];
        this.date = details.details.split(">")[4].split("<")[0];
        this.status = details.status;
        this.showViewTransactionModal = true;
      },
      setAction(index) {
        this.activeRow = index;
        this.showActions = true;
      },

      gotoPage(page) {
        this.$emit("go-to-page", page);
      },
      requestRefund(details) {
        const misc = details.misc.split("/");
        const transactionId = misc[1];
        this.maxRefundAmount = parseFloat(
          details.amount.split("$")[1].replace(/,/g, "")
        );
        this.refundTransactionId = transactionId;
        this.showActions = false;
        this.showRefundModal = true;
      },
      validateForm() {
        this.v$.refundAmount.$touch();
        this.v$.$validate();
        if (this.v$.$errors.length === 0) {
          this.processRefund();
        }
      },
      async processRefund() {
        const object = {
          amount: this.refundAmount,
          id: this.refundTransactionId,
        };
        this.loading = true;
        try {
          const response = await this.getRefund(object);
          const { message } = response.data;
          this.showToast({
            description: message,
            display: true,
            type: "success",
          });
          this.loading = false;
        } catch (error) {
          this.loading = false;
          return error;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .transaction-table-wrapper {
    padding-bottom: 24px;
  }

  .table-container {
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
  }

  .transaction-table {
    width: 100%;
    min-width: max-content;
    border-collapse: collapse;
    
    @media (min-width: 1280px) {
      min-width: 100%;
    }
    
    &__head {
      background: #F8FAFC;
    }
    
    &__header-row {
      display: grid;
      column-gap: 1.5rem;
      padding: 16px 20px;
      border-bottom: 1px solid #E2E8F0;
    }
    
    &__header-cell {
      font-family: 'DM Sans', sans-serif;
      font-size: 13px;
      font-weight: 500;
      color: #64748B;
      text-align: left;
      
      &--center {
        text-align: center;
      }
    }
    
    &__body {
      background: #ffffff;
    }
    
    &__row {
      display: grid;
      column-gap: 1.5rem;
      padding: 16px 20px;
      border-bottom: 1px solid #E2E8F0;
      align-items: center;
      transition: background-color 0.15s ease;
      
      &:hover {
        background: #F8FAFC;
      }
      
      &:last-child {
        border-bottom: none;
      }
    }
    
    &__icon {
      display: flex;
      justify-content: flex-end;
    }
    
    &__icon-box {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &--credit {
        background: #ECFDF5;
        color: #059669;
      }
      
      &--debit {
        background: #FEF2F2;
        color: #DC2626;
      }
      
      &--default {
        background: #F1F5F9;
        color: #64748B;
      }
    }
    
    &__cell {
      align-self: center;
      min-width: 0;
    }

    &__cell-text {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #0F172A;
      margin: 0;
      white-space: normal;
      overflow-wrap: break-word;
      word-break: break-word;
      
      :deep(span) {
        font-family: 'DM Sans', sans-serif;
      }
      
      :deep(.tw-font-bold) {
        font-weight: 500;
      }
      
      :deep(.tw-text-black-light) {
        color: #64748B;
      }
      
      :deep(.tw-text-xs) {
        font-size: 12px;
      }
      
      :deep(.tw-text-error) {
        color: #DC2626;
      }
      
      :deep(.tw-text-success) {
        color: #059669;
      }
    }
  }
  
  .status-badge {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 20px;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    
    &--completed {
      background: #ECFDF5;
      color: #059669;
    }
    
    &--pending {
      background: #FEF9C3;
      color: #CA8A04;
    }
    
    &--failed {
      background: #FEF2F2;
      color: #DC2626;
    }
  }
  
  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    padding: 16px 20px 0;
  }
  
  // Modal Styles
  .modal-header {
    margin-bottom: 24px;
    
    &__title {
      font-family: 'DM Sans', sans-serif;
      font-size: 20px;
      font-weight: 500;
      color: #0F172A;
      margin: 0 0 8px 0;
    }
    
    &__ref {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #64748B;
      margin: 0;
    }
  }
  
  .modal-divider {
    height: 1px;
    background: #E2E8F0;
    margin-bottom: 20px;
  }
  
  .modal-row {
    display: flex;
    justify-content: space-between;
    padding: 16px 0;
    border-bottom: 1px solid #E2E8F0;
    
    &:last-child {
      border-bottom: none;
    }
    
    &__item {
      &--right {
        text-align: right;
      }
    }
    
    &__label {
      font-family: 'DM Sans', sans-serif;
      font-size: 12px;
      font-weight: 400;
      color: #64748B;
      margin: 0 0 4px 0;
    }
    
    &__value {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #0F172A;
      margin: 0;
      
      &--success {
        color: #059669;
      }
      
      &--warning {
        color: #CA8A04;
      }
      
      &--error {
        color: #DC2626;
      }
    }
  }
  
  // Refund Modal Styles
  .refund-modal__content {
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
  }
  
  .refund-modal__header {
    background: #F8FAFC;
    padding: 24px;
    text-align: center;
    border-bottom: 1px solid #E2E8F0;
  }
  
  .refund-modal__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0;
  }
  
  .refund-form {
    padding: 24px;
    
    &__info {
      margin-bottom: 16px;
    }
    
    &__label {
      font-family: 'DM Sans', sans-serif;
      font-size: 16px;
      font-weight: 500;
      color: #0F172A;
      margin: 0 0 4px 0;
    }
    
    &__hint {
      font-family: 'DM Sans', sans-serif;
      font-size: 13px;
      font-weight: 400;
      color: #64748B;
      margin: 0;
    }
    
    &__input-wrapper {
      margin-bottom: 20px;
    }
    
    &__error {
      font-family: 'DM Sans', sans-serif;
      font-size: 13px;
      font-weight: 400;
      color: #DC2626;
      margin: 0 0 8px 0;
    }
    
    &__error-amount {
      font-weight: 500;
    }
    
    &__input {
      width: 100%;
      padding: 14px 16px;
      background: #ffffff;
      border: 1px solid #E2E8F0;
      border-radius: 10px;
      font-family: 'DM Sans', sans-serif;
      font-size: 18px;
      font-weight: 500;
      color: #0F172A;
      transition: all 0.2s ease;
      
      &::placeholder {
        color: #94A3B8;
      }
      
      &:focus {
        outline: none;
        border-color: #4D84FF;
        box-shadow: 0 0 0 3px rgba(77, 132, 255, 0.1);
      }
      
      &--error {
        border-color: #DC2626;
        
        &:focus {
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
        }
      }
    }
    
    &__checkbox-wrapper {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 24px;
    }
    
    &__checkbox {
      width: 20px;
      height: 20px;
      border-radius: 4px;
      border: 1px solid #E2E8F0;
      cursor: pointer;
      accent-color: #4D84FF;
    }
    
    &__checkbox-label {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #0F172A;
      cursor: pointer;
    }
    
    &__checkbox-amount {
      font-weight: 500;
    }
    
    &__submit {
      width: 100%;
      padding: 14px 24px;
      background: #4D84FF;
      border: none;
      border-radius: 8px;
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #ffffff;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:hover {
        background: #3B6FE8;
      }
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
</style>
